import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSavedMethods,
  initializePaymentSession,
} from "../../redux/slices/paymentLinkingSlice";
import { paymentLinkingApi } from "../services/paymentLinkingApi";
import {
  Avatar,
  CustomerDetails,
  ErrorState,
  LoadingState,
  PaymentSummary,
  PrimaryButton,
} from "../components";

/**
 * GoPay tokenization linking panel.
 *
 * Three-phase UX:
 *
 *   Phase 1 — Pre-redirect:
 *     The customer lands here either via the gopay-only fast-path
 *     from PaymentInformation, or by selecting "GoPay" on the
 *     multi-method picker. Tap the CTA → POST /gopay/initiate →
 *     redirect window.location to the Gojek-app activation URL.
 *
 *   Phase 2 — Post-redirect:
 *     Customer returns to /b2b/linking/{code}/result after
 *     authorizing in Gojek. Either GoPayLinking is re-mounted
 *     (deep link) or PaymentResult takes over. To converge the
 *     UX, this page polls the saved-methods endpoint when it
 *     detects ?from_gopay=1 in the URL, surfacing "Linking your
 *     GoPay…" until the saved instrument flips to ACTIVE
 *     (account-link notification handled on the backend).
 *
 *   Phase 3 — Already-linked fast-path:
 *     On EVERY mount we also run a single saved-methods probe so
 *     the UI can detect a customer who reaches this page after
 *     binding is already done (browser-back, bookmark, deep link
 *     from elsewhere, the PaymentInformation fast-path, …) and
 *     render the conclusive "Done" panel instead of the misleading
 *     "Continue to GoPay" CTA. Without this, the customer would
 *     see the pay button, click it, and (although the backend now
 *     short-circuits the actual charge — see
 *     MidtransGoPayAdapter::initiateAccountLinking and
 *     WebhookHandler::handleAccountLinkSuccess) repeatedly
 *     experience the bug we shipped a backend fix for. This
 *     frontend probe closes the loop end-to-end and matches the
 *     credit-card UX where a successful payment immediately
 *     navigates to a result page with no further pay action.
 */
const GoPayLinking = () => {
  const { linkCode } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { paymentLink, loading: globalLoading, error } = useSelector((state) => ({
    paymentLink: state.paymentLinking.paymentLink,
    loading:
      state.paymentLinking.loading.details ||
      state.paymentLinking.loading.initialize,
    error: state.paymentLinking.error,
  }));

  const [initiating, setInitiating] = useState(false);
  const [initiateError, setInitiateError] = useState(null);
  const [polling, setPolling] = useState(false);
  const [pollResult, setPollResult] = useState(null);
  // True while the one-shot mount probe is in flight. We hide the
  // "Continue to GoPay" CTA during this small window so a customer
  // whose instrument is already ACTIVE doesn't see a stale pay
  // button flash before the linked-state UI takes over.
  const [initialChecking, setInitialChecking] = useState(true);
  const pollTimer = useRef(null);
  const pollAttempts = useRef(0);

  // Bootstrap the session if the user deep-linked here directly.
  useEffect(() => {
    if (!paymentLink) {
      dispatch(initializePaymentSession({ linkCode })).catch(() => undefined);
    }
  }, [linkCode, paymentLink, dispatch]);

  // Mount-time probe + post-Gojek polling.
  //
  // Two responsibilities:
  //
  //   1. ALWAYS run a single saved-methods probe so the page can
  //      detect "this customer is already linked" regardless of
  //      whether they arrived via the post-Gojek redirect, a
  //      browser back/forward, a bookmarked URL, or the
  //      PaymentInformation gopay-only fast-path. If we find an
  //      ACTIVE GoPay instrument, immediately switch to the
  //      "linked" UI — no Continue button, no clickable pay action.
  //      This is the frontend half of the double-charge defence;
  //      the backend half lives in MidtransGoPayAdapter +
  //      WebhookHandler.
  //
  //   2. If we DIDN'T find an active instrument AND the URL has
  //      ?from_gopay=1 (the customer just bounced back from Gojek
  //      and the account-link webhook may not have hit our backend
  //      yet), kick off the existing 3s-interval polling loop so
  //      the UI flips to "linked" as soon as the webhook lands.
  useEffect(() => {
    let cancelled = false;
    const runInitialCheck = async () => {
      try {
        const result = await dispatch(fetchSavedMethods(linkCode)).unwrap();
        if (cancelled) return;
        const methods = result?.saved_methods || [];
        const activeGoPay = methods.find(
          (m) =>
            (m.type === "ewallet" || m.type === "gopay") && m.status === "active"
        );
        if (activeGoPay) {
          setPollResult({ status: "linked", instrument: activeGoPay });
          setInitialChecking(false);
          return;
        }
        // Not linked yet — if the customer just came back from
        // Gojek, start the polling loop.
        const params = new URLSearchParams(window.location.search);
        if (params.get("from_gopay") === "1") {
          setInitialChecking(false);
          startPollingForLinkedInstrument();
          return;
        }
        setInitialChecking(false);
      } catch (err) {
        if (cancelled) return;
        // Probe failure must not block the UI — fall back to the
        // legacy from_gopay-only behaviour so a transient network
        // hiccup never traps the customer on a perpetual spinner.
        console.error("[GoPayLinking] initial saved-methods probe failed", err);
        const params = new URLSearchParams(window.location.search);
        if (params.get("from_gopay") === "1") {
          startPollingForLinkedInstrument();
        }
        setInitialChecking(false);
      }
    };
    runInitialCheck();
    return () => {
      cancelled = true;
      if (pollTimer.current) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPollingForLinkedInstrument = () => {
    if (pollTimer.current) return;
    setPolling(true);
    pollAttempts.current = 0;

    const tick = async () => {
      pollAttempts.current += 1;
      try {
        const result = await dispatch(fetchSavedMethods(linkCode)).unwrap();
        const methods = result?.saved_methods || [];
        const activeGoPay = methods.find(
          (m) => (m.type === "ewallet" || m.type === "gopay") && m.status === "active"
        );
        if (activeGoPay) {
          setPollResult({ status: "linked", instrument: activeGoPay });
          stopPolling();
          return;
        }
        // Stop after ~2 minutes (40 attempts at 3s each)
        if (pollAttempts.current >= 40) {
          setPollResult({
            status: "timeout",
            message:
              "Still waiting on confirmation from GoPay. You can close this page — the subscription will start as soon as the binding is confirmed.",
          });
          stopPolling();
        }
      } catch (err) {
        console.error("[GoPayLinking] poll error", err);
      }
    };

    tick();
    pollTimer.current = setInterval(tick, 3000);
  };

  const stopPolling = () => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
    setPolling(false);
  };

  const handleLinkGoPay = async () => {
    // Defensive double-click / re-entry guard. The backend is
    // idempotent against repeat /gopay/initiate calls (it now
    // short-circuits when the saved instrument is already ACTIVE),
    // but we still want to lock the UI immediately so a customer
    // hammering the button doesn't flood the rate limiter or fire
    // multiple redirects in flight.
    if (initiating || polling || pollResult?.status === "linked") {
      return;
    }
    setInitiateError(null);
    setInitiating(true);
    const resultUrl = `${window.location.origin}/b2b/linking/${linkCode}/result?from_gopay=1`;
    try {
      const response = await paymentLinkingApi.initiateGoPayLinking(linkCode, {
        redirectUrl: resultUrl,
      });
      const data = response?.data || {};
      // Already-linked fast-path: when the customer's phone is
      // already bound to this merchant (either from a prior
      // subscription on the same account OR from a successful
      // earlier click on THIS link), the backend returns
      // `already_linked: true` with no activation_url (Midtrans
      // gives us none — there's nothing for the customer to
      // authorize). Switch to the "linked" UI and route to the
      // result page.
      if (data.already_linked === true) {
        setPollResult({
          status: "linked",
          message:
            "Your GoPay account is already linked. Redirecting to your subscription…",
        });
        // If we're already on the result page (e.g. the customer
        // bounced back from Gojek and is re-clicking), assigning
        // `window.location.href` to the same URL is a no-op in
        // most browsers, leaving the user staring at the linking
        // page. Force a same-document navigation via the SPA
        // router instead so the result page mounts and takes over
        // the polling. Then keep `initiating` true so the button
        // stays disabled even if the navigation is debounced.
        const targetPath = `/b2b/linking/${linkCode}/result?from_gopay=1`;
        if (window.location.pathname + window.location.search === targetPath) {
          // No URL change to perform; just lock down the UI and
          // let the polling state below ("linked") render the
          // "Done" CTA.
          setInitiating(false);
        } else {
          navigate(targetPath, { replace: true });
        }
        return;
      }
      const activationUrl = data.activation_url;
      if (!activationUrl) {
        throw new Error("Backend did not return a GoPay activation URL");
      }
      // Redirect the whole tab to the Gojek app — on mobile this
      // deep-links into the Gojek native app, on desktop it falls
      // back to the QR / "open in app" landing page Midtrans hosts.
      window.location.href = activationUrl;
    } catch (err) {
      console.error("[GoPayLinking] initiate failed", err);
      setInitiateError(
        err?.message || "Failed to start GoPay linking. Please try again."
      );
      setInitiating(false);
    }
  };

  if (!paymentLink && globalLoading) {
    return <LoadingState />;
  }

  if (error && !paymentLink) {
    return <ErrorState error={error} />;
  }

  if (!paymentLink) {
    return null;
  }

  const { merchant, customer, items, pricing } = paymentLink;
  const phoneMasked = customer?.phone
    ? customer.phone.replace(/^(\+?\d{2,4})\d+(\d{4})$/, "$1*****$2")
    : null;

  // Linked-state predicate. Used to swap the WHOLE bottom half of
  // the page from the "Continue to GoPay" affordance to a
  // conclusive "Done" panel — same end-state UX as credit-card
  // PaymentResult. We DO NOT render the payment summary or the
  // Continue CTA in this state, since both invite the customer
  // to take an action that no longer makes sense (the recurring
  // schedule will fire automatically; manual re-initiate would
  // either no-op on the backend or, if our defences ever break,
  // re-charge a future bill).
  const isLinked = pollResult?.status === "linked";

  // If the merchant configured a post-link redirect (settings.redirect_url),
  // surface it as the Done CTA target so the customer is sent back to
  // the merchant flow instead of to PaymentInformation, which would
  // immediately fast-path them right back here for any gopay-only
  // recurring link.
  const merchantRedirectUrl = paymentLink?.settings?.redirect_url || null;
  const handleDoneClick = () => {
    if (merchantRedirectUrl) {
      window.location.href = merchantRedirectUrl;
      return;
    }
    // No merchant redirect configured — try to close the tab
    // (works if this tab was opened via window.open, e.g. from
    // the payment-link sender). If the browser refuses to close
    // (most common case: user navigated here directly), there's
    // nothing left to do; the success message is the terminal UI.
    window.close();
  };

  const renderPollingState = () => (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 mb-6">
      <p className="text-sm font-medium text-amber-900">
        Confirming your GoPay link…
      </p>
      <p className="text-xs mt-1 text-amber-800">
        We're waiting for Gojek to confirm the binding. This usually takes a
        few seconds.
      </p>
    </div>
  );

  const renderPollResult = () => {
    if (!pollResult) return null;
    // Note: the "linked" branch is rendered separately by the
    // dedicated done-state JSX below (not here) so we can hide the
    // payment summary + Continue CTA entirely. Only non-linked
    // pollResult variants (e.g. "timeout") flow through here.
    if (pollResult.status === "linked") {
      return null;
    }
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 mb-6">
        <p className="text-sm font-medium text-slate-800">
          {pollResult.message}
        </p>
      </div>
    );
  };

  const renderLinkedDonePanel = () => (
    <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
      <div
        className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
        style={{ backgroundColor: "#ECFDF5" }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-10 h-10"
          fill="none"
          viewBox="0 0 24 24"
          stroke="#01793B"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h1 className="text-lg sm:text-xl font-bold mb-2" style={{ color: "#3B3B3B" }}>
        GoPay Linked Successfully
      </h1>
      <p className="text-xs sm:text-sm mb-6" style={{ color: "#515164" }}>
        Your GoPay account is linked to this subscription. Future bills will be
        charged automatically on schedule — there's nothing else for you to do
        here.
      </p>
      <PrimaryButton onClick={handleDoneClick}>
        {merchantRedirectUrl ? "Return to Merchant" : "Done"}
      </PrimaryButton>
      <p className="text-[10px] sm:text-xs mt-4" style={{ color: "#828282" }}>
        You can unlink this account any time from the Gojek app.
      </p>
    </div>
  );

  return (
    <div
      className="min-h-screen py-12 px-4"
      style={{ backgroundColor: "#F9FAFC" }}
    >
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Avatar
            name={merchant?.name}
            imageUrl={merchant?.logo_url}
            size="md"
          />
          <h1
            className="text-lg sm:text-xl font-bold"
            style={{ color: "#3B3B3B" }}
          >
            {merchant?.name || "Merchant"}
          </h1>
        </div>

        {isLinked ? (
          // Conclusive done-state — mirrors the credit-card
          // PaymentResult success view. We deliberately do NOT
          // render the customer details / payment summary / pay
          // button here, so a customer who lands on this page
          // after linking is complete (browser-back, bookmark,
          // PaymentInformation fast-path, …) cannot manually
          // re-trigger the GoPay charge flow.
          renderLinkedDonePanel()
        ) : (
          <>
            <div className="mb-6">
              <h2
                className="text-xl sm:text-2xl font-bold mb-1"
                style={{ color: "#3B3B3B" }}
              >
                Link Your GoPay
              </h2>
              <p className="text-xs sm:text-sm" style={{ color: "#515164" }}>
                We'll link your GoPay account so future bills can be charged
                automatically. You can unlink it any time from the Gojek app.
              </p>
            </div>

            <CustomerDetails customer={customer} />

            <PaymentSummary items={items} pricing={pricing} />

            <div className="rounded-lg border border-slate-200 bg-white p-4 mb-4">
              <p className="text-sm font-semibold text-slate-800 mb-1">
                Pay with GoPay
              </p>
              <p className="text-xs text-slate-600">
                Tapping continue will open the Gojek app on your phone. Confirm
                the linking in Gojek and you'll be redirected back here
                automatically.
              </p>
              {phoneMasked && (
                <p className="text-xs text-slate-500 mt-2">
                  Phone on file:{" "}
                  <span className="font-mono text-slate-700">{phoneMasked}</span>
                </p>
              )}
            </div>

            {polling && renderPollingState()}
            {renderPollResult()}

            {initiateError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 mb-4">
                <p className="text-sm text-red-800">{initiateError}</p>
              </div>
            )}

            {/* Hide the Continue CTA while the mount-time linked
                probe is still in flight. Without this, a customer
                whose instrument is already ACTIVE would see a
                stale "Continue to GoPay" button flash for a few
                hundred milliseconds before the linked-state UI
                takes over. */}
            {!initialChecking && (
              <PrimaryButton
                onClick={handleLinkGoPay}
                disabled={initiating || polling}
              >
                {initiating ? "Opening Gojek…" : "Continue to GoPay"}
              </PrimaryButton>
            )}
            {initialChecking && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center">
                <p className="text-sm text-slate-700">
                  Checking your GoPay link status…
                </p>
              </div>
            )}
          </>
        )}

        <div className="text-center mt-4">
          <p className="text-[10px] sm:text-xs" style={{ color: "#828282" }}>
            © 2025 {process.env.REACT_APP_NAME || "SingaEngine"}. All Rights
            Reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GoPayLinking;
